import React, { useRef } from 'react';
import { Card, Button, Space, Typography, message } from 'antd';
import { PageLoading } from '@/shared/components/ui';
import { useTranslation } from 'react-i18next';
import { ExportOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

// 导入自定义组件和hooks
import OrderSearchForm from './query/components/OrderSearchForm';
import OrderList from './query/components/OrderList';
import OrderPagination from './query/components/OrderPagination';
import OrderFilters from './query/components/OrderFilters';
import { useOrderSearch } from './query/hooks/useOrderSearch';
import { useOrderExport } from './query/hooks/useOrderExport';
import { useCurrentUser } from '../../stores/authStore';
import type { ExtendedSearchOrdersParams } from './query/types';
import type { OrderSearchFormRef } from './query/components/OrderSearchForm';

const { Title, Text } = Typography;

interface OrderQueryPageProps {
  companyId: number;
  readOnly?: boolean;
  canUpdateStatus?: boolean;
  canAddNotes?: boolean;
  canExport?: boolean;
  canDelete?: boolean;
}

const OrderQueryPage: React.FC<OrderQueryPageProps> = ({
  companyId,
  readOnly = false,
  canUpdateStatus = false,
  canAddNotes = false,
  canExport = true,
  canDelete = false,
}) => {
  const { t } = useTranslation(['modules', 'common']);
  const navigate = useNavigate();
  const searchFormRef = useRef<OrderSearchFormRef>(null);

  // 获取当前用户信息
  const currentUser = useCurrentUser();
  const userRole = currentUser?.role || 'user';

  // 使用自定义搜索hook
  const {
    params,
    orders,
    totalCount,
    isLoading,
    error,
    paginationInfo,
    activeFilters,
    updateParams,
    resetParams,
    refetch,
    handlePageChange,
    handlePageSizeChange,
    applyQuickFilter,
    clearFilter,
    clearAllFilters
  } = useOrderSearch({
    company_id: companyId,
    limit: 20,
    offset: 0
  });

  // 使用导出hook
  const { exporting, exportOrders, exportSelected, batchExport } = useOrderExport();

  // 处理查看订单详情
  const handleViewDetail = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  // 处理编辑订单
  const handleEdit = (orderId: string) => {
    // 编辑功能开发中
    message.info(t('modules:order.query.list.editMessage', '编辑功能开发中'));
    // 暂时不跳转
    // navigate(`/orders/${orderId}/edit`);
  };

  // 处理删除订单
  const handleDelete = async (orderId: string) => {
    // 这里应该调用删除订单的API
    message.info(t('modules:order.query.list.deleteMessage', '删除功能开发中'));
  };

  // 处理更新订单状态
  const handleUpdateStatus = async (orderId: string, newStatus: string) => {
    // 这里应该调用更新订单状态的API
    message.info(t('modules:order.query.list.statusUpdateMessage', '状态更新功能开发中'));
  };

  // 处理导出
  const handleExport = () => {
    exportOrders(params, {
      format: 'csv',
      includeAllColumns: true
    });
  };

  // 处理批量导出
  const handleBatchExport = (selectedOrderIds: string[]) => {
    batchExport(selectedOrderIds, params, {
      format: 'excel',
      includeAllColumns: true
    });
  };

  // 处理行选择变化
  const handleSelectionChange = (selectedRowKeys: React.Key[], selectedRows: any[]) => {
    // 可以在这里处理选中的行数据
    console.log('Selected rows:', selectedRows);
  };

  // 处理行点击
  const handleRowClick = (record: any, index: number) => {
    handleViewDetail(record.order_id);
  };

  // 显示错误
  if (error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Title level={4} type="danger">
            {t('modules:order.query.loadError', '加载订单数据失败')}
          </Title>
          <Text type="secondary">
            {error instanceof Error ? error.message : String(error)}
          </Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => refetch()}>
              {t('common:retry', '重试')}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (isLoading && orders.length === 0) {
    return <PageLoading />;
  }

  return (
    <div className="order-query-page">
      {/* 页面标题 */}
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ marginBottom: 8 }}>
          {t('modules:order.query.title', '订单查询')}
        </Title>
        <Text type="secondary">
          {t('modules:order.query.subtitle', '查询和管理所有订单')}
          {readOnly && ` (${t('modules:order.query.readOnly', '只读模式')})`}
        </Text>
      </div>

      {/* 筛选条件 */}
      <OrderFilters
        params={params}
        onFilterChange={updateParams}
        onClearAll={clearAllFilters}
        onClearFilter={clearFilter}
        showQuickFilters={true}
        showPresets={true}
        showActiveFilters={true}
        loading={isLoading}
        disabled={readOnly}
        userRole={userRole}
      />

      {/* 搜索表单 */}
      <OrderSearchForm
        ref={searchFormRef}
        initialParams={params}
        onSearch={updateParams}
        onReset={resetParams}
        loading={isLoading}
        disabled={readOnly}
      />

      {/* 订单列表 */}
      <Card style={{ marginBottom: 8 }}>
        <OrderList
          data={orders}
          loading={isLoading}
          readOnly={readOnly}
          canUpdateStatus={canUpdateStatus}
          canAddNotes={canAddNotes}
          canDelete={canDelete}
          showSelection={!readOnly}
          showActions={true}
          onRefresh={() => refetch()}
          onViewDetail={handleViewDetail}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onUpdateStatus={handleUpdateStatus}
          onSelectionChange={handleSelectionChange}
          onRowClick={handleRowClick}
        />
      </Card>

      {/* 分页 */}
      {totalCount > 0 && (
        <OrderPagination
          total={totalCount}
          current={paginationInfo.currentPage}
          pageSize={paginationInfo.pageSize}
          onChange={handlePageChange}
          onShowSizeChange={handlePageSizeChange}
          showTotal={true}
          showSizeChanger={true}
          showQuickJumper={true}
          disabled={readOnly || isLoading}
        />
      )}

      {/* 导出按钮 */}
      {canExport && !readOnly && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Button
              type="primary"
              icon={<ExportOutlined />}
              onClick={handleExport}
              loading={exporting}
              disabled={orders.length === 0}
            >
              {t('modules:order.query.export', '导出订单')}
            </Button>
            <Button
              type="default"
              icon={<ExportOutlined />}
              onClick={() => {
                // 这里可以触发批量导出
                const selectedIds: string[] = []; // 需要从OrderList获取选中的ID
                if (selectedIds.length > 0) {
                  handleBatchExport(selectedIds);
                } else {
                  message.warning(t('modules:order.query.exportNoSelection', '请先选择要导出的订单'));
                }
              }}
              loading={exporting}
              disabled={orders.length === 0}
            >
              {t('modules:order.query.exportSelected', '导出选中')}
            </Button>
          </Space>
        </div>
      )}

      {/* 统计信息 */}
      <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#fafafa', borderRadius: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text type="secondary">
              {t('modules:order.query.stats.title', '统计信息')}:
            </Text>
            <div style={{ marginTop: 8 }}>
              <Space size="large">
                <div>
                  <Text type="secondary">{t('modules:order.query.stats.total', '总订单数')}:</Text>
                  <Text strong style={{ marginLeft: 8 }}>{totalCount}</Text>
                </div>
                <div>
                  <Text type="secondary">{t('modules:order.query.stats.currentPage', '当前页')}:</Text>
                  <Text strong style={{ marginLeft: 8 }}>{orders.length}</Text>
                </div>
                <div>
                  <Text type="secondary">{t('modules:order.query.stats.activeFilters', '筛选条件')}:</Text>
                  <Text strong style={{ marginLeft: 8 }}>{activeFilters.length}</Text>
                </div>
              </Space>
            </div>
          </div>
          <div>
            <Button onClick={() => refetch()} loading={isLoading}>
              {t('modules:order.query.refresh', '刷新数据')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderQueryPage;